<template>
    <v-dialog
        :model-value="isOpen"
        eager
        max-width="800"
        scrollable
        transition="slide-y-transition"
        @click:outside="closeDialog"
        @keydown.esc="closeDialog"
    >
        <v-card>
            <v-card-title class="text-subtitle-1">
                <template v-if="currentStep === 1">
                    {{ t('common.dashboard.dialogs.delayedProductsTitle') }} {{ todayLabel }}: <strong>{{ partner }}</strong>
                </template>
                <template v-else>
                    {{ t('common.dashboard.dialogs.delayedOrdersTitle') }} {{ todayLabel }}:
                    <strong>{{ partner }} / {{ product }}</strong>
                </template>
            </v-card-title>

            <v-card-text class="pa-0">
                <v-window v-model="currentStep">
                    <v-window-item :value="1">
                        <v-data-table
                            :headers="productsHeaders"
                            :items="ordersProductsDelayed"
                            class="fixed-header-table"
                            density="compact"
                            fixed-header
                            item-value="prodName"
                        >
                            <template #item.quantity="{ item }">{{ formatNumber(item.quantity) }}</template>
                            <template #item.details="{ item }">
                                <v-tooltip :text="t('common.dashboard.tooltips.showDetails')" location="top">
                                    <template #activator="{ props: tooltipProps }">
                                        <v-btn
                                            v-bind="tooltipProps"
                                            color="blue"
                                            icon="mdi-ballot"
                                            size="small"
                                            variant="text"
                                            @click="getOrderList(item)"
                                        />
                                    </template>
                                </v-tooltip>
                            </template>
                        </v-data-table>
                    </v-window-item>

                    <v-window-item :value="2">
                        <v-card-actions>
                            <v-btn color="primary" prepend-icon="mdi-arrow-left" variant="text" @click="currentStep = 1">
                                {{ t('common.dashboard.buttons.backToProducts') }}
                            </v-btn>
                        </v-card-actions>
                        <v-data-table
                            :headers="ordersHeaders"
                            :items="ordersList"
                            class="fixed-header-table"
                            density="compact"
                            fixed-header
                            item-value="orderNumber"
                        >
                            <template #item.sendDate="{ item }">{{ formatDateForDisplay(item.sendDate) }}</template>
                            <template #item.quantity="{ item }">{{ formatNumber(item.quantity) }}</template>
                        </v-data-table>
                    </v-window-item>
                </v-window>
            </v-card-text>

            <v-card-actions>
                <v-btn color="error" variant="text" @click="closeDialog">{{ t('common.dashboard.buttons.close') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore } from '@/stores'
import type { DataTableHeader } from '@/types/dataTable'
import { formatDateForDisplay, formatNumber } from '@/views/Dashboard/dashboardUtils'

type DelayedProductRow = {
    partner?: string
    productId?: string | number
    prodName?: string
    toSend?: unknown
    quantity?: unknown
}

type DelayedOrderDetailRow = {
    orderNumber?: string
    customerNumber?: string
    quantity?: unknown
    sendDate?: string
}

// 2. Props / Emits
withDefaults(
    defineProps<{
        isOpen?: boolean
        partner?: string
        ordersProductsDelayed?: DelayedProductRow[]
    }>(),
    { isOpen: false, partner: '', ordersProductsDelayed: () => [] }
)

const emit = defineEmits<{
    'close-delayed-orders-products-dialog': []
}>()

// 3. Constants
const productsHeaders: DataTableHeader[] = [
    { title: 'Produkt', key: 'prodName', sortable: false },
    { title: 'Do wysyłki', key: 'toSend', sortable: false, align: 'center' },
    { title: 'Nakład', key: 'quantity', sortable: false, align: 'center' },
    { title: 'Szczegóły', key: 'details', sortable: false, align: 'center' }
]

const ordersHeaders: DataTableHeader[] = [
    { title: 'Zamówienie', key: 'orderNumber', sortable: false },
    { title: 'Nr klienta', key: 'customerNumber', sortable: false },
    { title: 'Nakład', key: 'quantity', sortable: false },
    { title: 'Realizacja', key: 'sendDate', sortable: false }
]

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const currentStep = ref(1)
const ordersList = ref<DelayedOrderDetailRow[]>([])
const product = ref('')

// 7. Computed
const todayLabel = computed(() => formatDateForDisplay(new Date()))

// 8. Handlers / methods
async function getOrderList(productItem: DelayedProductRow): Promise<void> {
    try {
        const response = await dashboardStore.getOrdersProductsDelayedByOrder({
            query: `partner=${productItem.partner}&productId=${productItem.productId}`
        })

        ordersList.value = (response?.data?.results || []) as DelayedOrderDetailRow[]
        product.value = productItem.prodName || ''
        currentStep.value = 2
    } catch (error) {
        errorHandler(error)
    }
}

function resetState(): void {
    currentStep.value = 1
    ordersList.value = []
    product.value = ''
}

function closeDialog(): void {
    resetState()
    emit('close-delayed-orders-products-dialog')
}
</script>
